import React, { useEffect, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { Switch } from "../components/ui/switch";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { features, faq } from "../mock/mock";

const LOGO_URL = "finaldraft.png";


// ---------- UTILS ----------
const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`section-container ${className}`}>{children}</section>
);

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("reveal-in");
        });
      },
      { threshold: 0.2 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return ref;
};

// ---------- EMAIL CAPTURE ----------
const EmailCapture = ({ variant = "primary" }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [showName, setShowName] = useState(false);
  const [isBiker, setIsBiker] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    const stored = localStorage.getItem("rydnet_email");
    if (stored) {
      const parsed = JSON.parse(stored);
      setEmail(parsed.email || "");
      setName(parsed.name || "");
      if (typeof parsed.isBiker === "boolean") setIsBiker(parsed.isBiker);
      setSaved(true);
    }
  }, []);

  useEffect(() => {
    const onJoined = () => setSaved(true);
    window.addEventListener("rydnet_joined", onJoined);
    return () => window.removeEventListener("rydnet_joined", onJoined);
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email.");
      return;
    }
    if (isBiker === null) {
      toast.error("Select if you're a biker — toggle the switch.");
      return;
    }
    setLoading(true);
    try {
      formRef.current?.submit();
      const payload = { email, name, isBiker, at: new Date().toISOString() };
      localStorage.setItem("rydnet_email", JSON.stringify(payload));
      setSaved(true);
      window.dispatchEvent(new Event("rydnet_joined"));
      setTimeout(() => {
        setLoading(false);
        toast.success("Submitted. Check your email to confirm.");
      }, 800);
    } catch {
      setLoading(false);
      toast.error("Could not submit. Try again.");
    }
  };

  return (
    <>
      <form
        ref={formRef}
        action="https://gmail.us5.list-manage.com/subscribe/post?u=bde2c4d011343b581a720d90a&id=da8d1001f7&f_id=00b37fe0f0"
        method="post"
        target="mc-target"
        noValidate
        className={`email-capture ${variant === "glass" ? "email-capture-glass" : ""}`}
        onSubmit={onSubmit}
      >
        <Input
          type="email"
          name="EMAIL"
          placeholder={saved ? "joined. welcome rider" : "enter email"}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onFocus={() => { setShowName(true); setShowHint(true); setTimeout(() => setShowHint(false), 3500); }}
          className="bg-zinc-900/70 border-red-600/50 text-white placeholder:text-red-300/50 focus-visible:ring-red-500"
          aria-label="Email address"
          required
          disabled={saved}
        />

        {(showName || name) && (
          <>
            <Input
              type="text"
              name="FNAME"
              placeholder={saved ? "joined. welcome rider" : "name (first or username)"}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="name-field bg-zinc-900/70 border-red-600/50 text-white placeholder:text-red-300/50 focus-visible:ring-red-500"
              disabled={saved}
            />
            <div className="biker-switch" data-full>
              <span className="label">Biker?</span>
              <Switch checked={!!isBiker} onCheckedChange={setIsBiker} disabled={saved} />
              <input type="hidden" name="BKR" value={isBiker === null ? "" : (isBiker ? "yes" : "no")} />
            </div>
          </>
        )}

        {showHint && !saved && <p className="join-hint font-courier">join our community for early access and updates</p>}

        <Button type="submit" disabled={loading || saved} className="cyber-btn">
          {saved ? "joined. welcome rider" : loading ? "Saving..." : "Join the Club"}
        </Button>
      </form>
      <iframe name="mc-target" title="mailchimp" style={{ display: "none" }} />
    </>
  );
};

// ---------- FEATURE CARD ----------
const FeatureCard = ({ icon, title, desc }) => {
  const Icon = Icons[icon] || Icons.CircleDashed;
  return (
    <Card className="feature-card">
      <CardContent className="p-6 flex items-start gap-4">
        <div className="icon-wrap"><Icon className="h-6 w-6" /></div>
        <div>
          <h3 className="feature-title">{title}</h3>
          <p className="feature-desc">{desc}</p>
        </div>
      </CardContent>
    </Card>
  );
};

// ---------- MAIN LANDING PAGE ----------
export default function LandingPage() {
  const sectionsRefs = {
    hero: useReveal(),
    features: useReveal(),
    community: useReveal(),
    preorders: useReveal(),
    faq: useReveal(),
    join: useReveal(),
  };

  const onScrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <NeonGrid />
      <Header onScrollTo={onScrollTo} />

      {/* HERO */}
      <Section id="hero" className="pt-24 pb-16">
        <div ref={sectionsRefs.hero} className="reveal">
          <div className="mx-auto max-w-6xl px-6 text-center">
            <h1 className="hero-title glitch" data-text="Welcome to the Club">Welcome to the Club</h1>
            <p className="hero-sub">
              RYDNET is the underground mesh for riders and the ones who live for the sound of a redline. No ads. No fluff. Just signal.
            </p>
            <div className="mt-8 flex justify-center"><EmailCapture variant="glass" /></div>
            <p className="mt-3 text-xs tracking-wider text-red-300/70">Early access — limited slots</p>
            <p className="mt-6 hero-blurb glitch font-courier" data-text="RYDNET is the rider’s digital playground: real-time proximity chat, epic group rides, clans, and exclusive track days. Join the brotherhood, claim your lane, and be part of the ride.">
              RYDNET is the rider’s digital playground: real-time proximity chat, epic group rides, clans, and exclusive track days. Join the brotherhood, claim your lane, and be part of the ride.
            </p>
          </div>
          <EKG />
        </div>
      </Section>

      {/* FEATURES */}
      <Section id="features" className="py-20">
        <div ref={sectionsRefs.features} className="reveal">
          <div className="mx-auto max-w-6xl px-6">
            <h2 className="section-title">Core Systems</h2>
            <p className="section-sub">Built for the night. Tuned for chaos. Friendly by default.</p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {features.map((f) => <FeatureCard key={f.key} {...f} />)}
            </div>
          </div>
        </div>
      </Section>

      {/* COMMUNITY */}
      <Section id="community" className="py-20">
        <div ref={sectionsRefs.community} className="reveal">
          <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="section-title">Community Uplink</h2>
              <p className="section-sub">First 1000 Discord members get premium access.</p>
              <div className="mt-6 flex items-center gap-4">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <a href="https://discord.gg/4uZSFR5wxU" target="_blank" rel="noreferrer" className="social-btn discord">
                        Discord
                      </a>
                    </TooltipTrigger>
                    <TooltipContent className="bg-zinc-900 text-white border-red-500">Join the first 1000.</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <a href="https://www.instagram.com/rydnet/" target="_blank" rel="noreferrer" className="social-btn instagram">
                         Instagram
                      </a>
                    </TooltipTrigger>
                    <TooltipContent className="bg-zinc-900 text-white border-red-500">Sneak peeks + drops.</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
            <Card className="p-6 bg-zinc-900/60 backdrop-blur-xl border-red-900/50 relative overflow-hidden">
              <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(60%_60%_at_20%_0%,rgba(220,38,38,0.15),transparent)]" />
              <h3 className="text-white font-orbitron text-lg tracking-widest">Signal Board</h3>
              <ul className="mt-4 space-y-3 text-red-200/90 text-sm font-courier">
                <li>• Premium gates open to first 1000 signups</li>
                <li>• Ride intel, drops, and meetups</li>
                <li>• Early clan reservations available</li>
              </ul>
            </Card>
          </div>
        </div>
      </Section>

      {/* PREORDERS */}
      <Section id="preorders" className="py-16">
        <div ref={sectionsRefs.preorders} className="reveal">
          <div className="mx-auto max-w-6xl px-6">
            <div className="preorder-banner">
              <span className="glitch" data-text="Preorders + Custom Tags Coming Soon">Preorders + Custom Tags Coming Soon</span>
            </div>
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section id="faq" className="py-20">
        <div ref={sectionsRefs.faq} className="reveal">
          <div className="mx-auto max-w-4xl px-6">
            <h2 className="section-title">Signal Check</h2>
            <Accordion type="single" collapsible className="mt-8">
              {faq.map((item, idx) => (
                <AccordionItem key={idx} value={`item-${idx}`} className="accordion-item">
                  <AccordionTrigger className="accordion-trigger">{item.q}</AccordionTrigger>
                  <AccordionContent className="accordion-content">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section id="join" className="py-20">
        <div ref={sectionsRefs.join} className="reveal">
          <div className="mx-auto max-w-xl px-6 text-center">
            <h2 className="section-title">Plug Into The Grid</h2>
            <p className="section-sub">No followers. No algorithms. Pure velocity. Join the brotherhood.</p>
            <div className="mt-8 flex justify-center"><EmailCapture /></div>
          </div>
        </div>
      </Section>

      <Footer />
      <Toaster richColors position="top-center" />
    </div>
  );
}

// ---------- HEADER & FOOTER ----------
const Header = ({ onScrollTo }) => (
  <header className="site-header fixed top-0 inset-x-0 z-50 border-b border-red-900/40 bg-zinc-950/70 backdrop-blur-xl">
    <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <a href="#hero" aria-label="RYDNET home" className="inline-flex items-center">
          <span className="logo-badge"><img src={LOGO_URL} alt="RYDNET logo" className="logo-mark" /></span>
        </a>
        <span className="font-orbitron tracking-widest text-white text-xs md:text-sm">RYDNET</span>
      </div>
      <nav className="hidden md:flex items-center gap-6 text-sm">
        {[
          ["Features", "features"],
          ["Community", "community"],
          ["Preorders", "preorders"],
          ["FAQ", "faq"],
        ].map(([label, id]) => (
          <button key={id} onClick={() => onScrollTo(id)} className="nav-link">{label}</button>
        ))}
        <button onClick={() => onScrollTo("join")} className="cyber-btn h-9 px-4">Join</button>
      </nav>
    </div>
  </header>
);

const Footer = () => (
  <footer className="py-8 border-t border-red-900/40 bg-zinc-950/80">
    <div className="mx-auto max-w-6xl px-6 text-xs text-red-300/70 tracking-widest flex items-center justify-between">
      <span>POWERED BY A70M0S INDUSTRIES</span>
      <span>&copy; {new Date().getFullYear()} RYDNET</span>
    </div>
  </footer>
);

// ---------- DECORATIVE COMPONENTS ----------
const NeonGrid = () => (
  <div className="neon-grid fixed inset-0 pointer-events-none bg-zinc-900/10"></div>
);

const EKG = () => (
  <div className="ekg-line mt-8 h-12 w-full bg-gradient-to-r from-red-600 via-white to-red-600 animate-pulse"></div>
);
